/**
 * ActivityRemindersPanel — inline panel inside Channel Activity page.
 *
 * Combines: (a) in-app Due/Upcoming reminders list scoped to THIS IB,
 * (b) iCal .ics subscription URL for iPhone/Mac/Google Calendar auto-sync,
 * (c) manual Telegram push per activity,
 * + inline "How to use" guide.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  CalendarClock, AlertTriangle, Send, Copy, CheckCircle, Loader2, HelpCircle,
  Smartphone, MonitorSmartphone, MessageSquare, Bell, ChevronDown, ChevronUp,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const timeLabel = (mins) => {
  if (mins < 0) return `${Math.abs(mins)}m ago`;
  if (mins < 60) return `in ${mins}m`;
  const h = Math.floor(mins / 60);
  const rest = mins % 60;
  if (h < 24) return rest ? `in ${h}h ${rest}m` : `in ${h}h`;
  const d = Math.floor(h / 24);
  return `in ${d}d ${h % 24}h`;
};

export default function ActivityRemindersPanel({ ibId }) {
  const [expanded, setExpanded] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [data, setData] = useState({ due: [], upcoming: [], overdue: [], counts: { due: 0, upcoming: 0, overdue: 0 } });
  const [loading, setLoading] = useState(false);
  const [pushingId, setPushingId] = useState(null);
  const [icalUrl, setIcalUrl] = useState('');
  const [copied, setCopied] = useState('');

  const fetchNow = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/activities/reminders`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        // Scope to THIS IB — bell has multi-IB; here we want only current IB
        const filt = (list) => list.filter((r) => r.ib_id === ibId);
        setData({
          due: filt(d.due || []),
          upcoming: filt(d.upcoming || []),
          overdue: filt(d.overdue || []),
          counts: {
            due: filt(d.due || []).length,
            upcoming: filt(d.upcoming || []).length,
            overdue: filt(d.overdue || []).length,
          },
        });
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [ibId]);

  useEffect(() => {
    // Fetch once on mount so the collapsed-header badge reflects current state.
    fetchNow();
  }, [fetchNow]);

  useEffect(() => {
    // Live polling only while expanded — saves requests without hiding the badge.
    if (!expanded) return;
    const t = setInterval(fetchNow, 60_000);
    return () => clearInterval(t);
  }, [fetchNow, expanded]);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/activities/ical-token`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const d = await res.json();
          // Per-channel subscription URL — filters activities to just this IB
          setIcalUrl(`${API}/api/activities/ical/${d.token}?ib_id=${encodeURIComponent(ibId)}`);
        }
      } catch { /* ignore */ }
    })();
  }, [ibId]);

  const pushTelegram = async (item) => {
    setPushingId(item.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/activities/telegram-reminder/${item.ib_id}/${item.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      const d = await res.json();
      if (d.count === 0) {
        toast.warning('Reminder sent to 0 channels — chat_id kosong. Sila set chat_id di Admin → Channel Links.');
      } else {
        toast.success(`Reminder sent to ${d.count} channel${d.count !== 1 ? 's' : ''}`);
      }
    } catch (e) {
      toast.error(e.message || 'Telegram push failed');
    } finally {
      setPushingId(null);
    }
  };

  const copyUrl = async (kind) => {
    if (!icalUrl) return;
    const url = kind === 'webcal' ? icalUrl.replace(/^https?:/, 'webcal:') : icalUrl;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(kind);
      toast.success(`${kind === 'webcal' ? 'webcal://' : 'HTTPS'} URL copied to clipboard`);
      setTimeout(() => setCopied(''), 2500);
    } catch {
      toast.error('Clipboard blocked. Copy manually: ' + url);
    }
  };

  const total = data.counts.due + data.counts.overdue;

  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]" data-testid="activity-reminders-panel">
      {/* Collapsed header — always visible */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between p-4 hover:bg-[hsl(var(--surface-elevated))] transition-colors rounded-xl"
        data-testid="btn-toggle-reminders"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#E63946]/15 flex items-center justify-center">
            <Bell size={16} className="text-[#E63946]" />
          </div>
          <div className="text-left">
            <p className="text-[12px] font-bold text-white flex items-center gap-2">
              Reminders & Calendar Sync
              {total > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-[#E63946] text-white text-[9px] font-bold" data-testid="reminder-badge">
                  {total} due
                </span>
              )}
            </p>
            <p className="text-[10px] text-[hsl(var(--text-muted))] mt-0.5">
              {data.counts.upcoming + data.counts.due} activity dalam 48 jam · sync to iPhone / Mac / Google Calendar / Telegram
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 size={12} className="animate-spin text-[hsl(var(--text-muted))]" />}
          {expanded ? <ChevronUp size={14} className="text-[hsl(var(--text-muted))]" /> : <ChevronDown size={14} className="text-[hsl(var(--text-muted))]" />}
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-[hsl(var(--border))]" data-testid="reminders-body">
          {/* Reminder lists */}
          <div className="p-4 space-y-3">
            {data.overdue.length > 0 && (
              <Section
                title="Overdue"
                icon={AlertTriangle}
                color="#EF4444"
                items={data.overdue}
                onPush={pushTelegram}
                pushingId={pushingId}
                tone="overdue"
              />
            )}
            {data.due.length > 0 && (
              <Section
                title="Due Soon"
                icon={CalendarClock}
                color="#F59E0B"
                items={data.due}
                onPush={pushTelegram}
                pushingId={pushingId}
                tone="due"
              />
            )}
            {data.upcoming.length > 0 && (
              <Section
                title="Upcoming (48h)"
                icon={CalendarClock}
                color="#3B82F6"
                items={data.upcoming}
                onPush={pushTelegram}
                pushingId={pushingId}
                tone="upcoming"
              />
            )}
            {total === 0 && data.upcoming.length === 0 && !loading && (
              <div className="p-6 text-center border border-dashed border-[hsl(var(--border))] rounded-lg">
                <CheckCircle size={20} className="text-[#10B981] mx-auto mb-2" />
                <p className="text-[11px] text-[hsl(var(--text-muted))]">
                  Tiada reminder pending untuk IB ini. Set <span className="text-white">&quot;Reminder&quot;</span> masa create activity untuk enable.
                </p>
              </div>
            )}
          </div>

          {/* Calendar Sync buttons */}
          <div className="p-4 bg-[hsl(var(--surface-elevated))] border-t border-[hsl(var(--border))]">
            <p className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--text-muted))] mb-2">
              Sync ke Calendar Aplikasi Anda
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <button
                onClick={() => copyUrl('webcal')}
                disabled={!icalUrl}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#E63946]/10 border border-[#E63946]/30 text-white hover:bg-[#E63946]/20 transition-all disabled:opacity-40"
                data-testid="btn-copy-webcal"
              >
                <div className="flex items-center gap-2">
                  <Smartphone size={14} className="text-[#E63946]" />
                  <span className="text-[11px] font-semibold">iPhone / Mac</span>
                </div>
                {copied === 'webcal' ? <CheckCircle size={12} className="text-[#10B981]" /> : <Copy size={12} className="text-[hsl(var(--text-muted))]" />}
              </button>
              <button
                onClick={() => copyUrl('https')}
                disabled={!icalUrl}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] text-white hover:border-[#E63946]/50 transition-all disabled:opacity-40"
                data-testid="btn-copy-https"
              >
                <div className="flex items-center gap-2">
                  <MonitorSmartphone size={14} className="text-[#3B82F6]" />
                  <span className="text-[11px] font-semibold">Google Calendar</span>
                </div>
                {copied === 'https' ? <CheckCircle size={12} className="text-[#10B981]" /> : <Copy size={12} className="text-[hsl(var(--text-muted))]" />}
              </button>
              <a
                href={icalUrl || '#'}
                download="underads-activities.ics"
                className={`flex items-center justify-between px-3 py-2 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] text-white hover:border-[#E63946]/50 transition-all ${!icalUrl ? 'opacity-40 pointer-events-none' : ''}`}
                data-testid="btn-download-ics"
              >
                <div className="flex items-center gap-2">
                  <ExternalLink size={14} className="text-[#8B5CF6]" />
                  <span className="text-[11px] font-semibold">Download .ics</span>
                </div>
              </a>
            </div>

            {/* Guide toggle */}
            <button
              onClick={() => setShowGuide((s) => !s)}
              className="mt-3 flex items-center gap-1.5 text-[10px] text-[hsl(var(--text-muted))] hover:text-white transition-all"
              data-testid="btn-toggle-guide"
            >
              <HelpCircle size={11} />
              <span>{showGuide ? 'Sembunyikan panduan' : 'Bagaimana cara guna? Klik untuk lihat panduan'}</span>
              {showGuide ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>

            {/* Inline guide */}
            {showGuide && (
              <div className="mt-3 space-y-3" data-testid="reminders-guide">
                <GuideStep
                  n="1"
                  icon={Bell}
                  color="#E63946"
                  title="Set Reminder masa create activity"
                  body={<>Bila klik <b>&quot;Create Activity&quot;</b>, dropdown <b>&quot;Reminder&quot;</b> akan muncul. Pilih bila anda nak reminder trigger (contoh: <em>1 hari sebelum</em>). Reminder 0 = disable.</>}
                />
                <GuideStep
                  n="2"
                  icon={Smartphone}
                  color="#E63946"
                  title="Sync ke iPhone / Mac Calendar (paling automatik)"
                  body={
                    <ol className="list-decimal ml-4 space-y-0.5">
                      <li>Klik <b>&quot;iPhone / Mac&quot;</b> button di atas untuk copy webcal:// URL.</li>
                      <li>Di iPhone: <b>Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar</b>. Paste URL, klik <b>Next → Save</b>.</li>
                      <li>Alternatif Mac: buka <b>Calendar.app → File → New Calendar Subscription</b>, paste URL.</li>
                      <li>Semua activity akan auto-appear dalam calendar anda. Reminder trigger native iOS (getar/bunyi) mengikut setting yang anda tetapkan.</li>
                    </ol>
                  }
                />
                <GuideStep
                  n="3"
                  icon={MonitorSmartphone}
                  color="#3B82F6"
                  title="Sync ke Google Calendar"
                  body={
                    <ol className="list-decimal ml-4 space-y-0.5">
                      <li>Klik <b>&quot;Google Calendar&quot;</b> button di atas.</li>
                      <li>Buka Google Calendar → sidebar kiri <b>&quot;Other calendars&quot;</b> → klik &quot;+&quot; → <b>From URL</b>.</li>
                      <li>Paste URL, klik <b>Add calendar</b>. Google akan refresh setiap ~4 jam.</li>
                    </ol>
                  }
                />
                <GuideStep
                  n="4"
                  icon={MessageSquare}
                  color="#10B981"
                  title="Push ke Telegram (one-click)"
                  body={
                    <>
                      Bila activity muncul di senarai atas (Due Soon / Upcoming), klik ikon <Send size={10} className="inline-block mx-0.5 text-[#E63946]" />
                      untuk broadcast reminder tu ke Telegram channel yang dah link dengan IB ni.
                      Kena set <b>chat_id</b> di <b>Admin → Channel Links</b> dulu.
                    </>
                  }
                />
                <GuideStep
                  n="5"
                  icon={ExternalLink}
                  color="#8B5CF6"
                  title="One-time .ics download"
                  body={<>Kalau nak import sekali sahaja (tanpa auto-sync), klik <b>&quot;Download .ics&quot;</b>. Boleh open dengan Outlook, Apple Calendar, Google Calendar, dll.</>}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, color, items, onPush, pushingId, tone }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1" style={{ color }}>
        <Icon size={10} /> {title} ({items.length})
      </p>
      <div className="space-y-1.5" data-testid={`reminders-section-${tone}`}>
        {items.map((r) => (
          <ReminderRow key={r.id} r={r} onPush={onPush} pushing={pushingId === r.id} tone={tone} />
        ))}
      </div>
    </div>
  );
}

function ReminderRow({ r, onPush, pushing, tone }) {
  const toneColor = { overdue: '#EF4444', due: '#F59E0B', upcoming: '#3B82F6' }[tone] || '#6B7280';
  return (
    <div className="p-2 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]/60" data-testid={`reminder-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-white truncate">{r.title || r.type}</p>
          <div className="flex items-center gap-2 mt-0.5">
            {r.channel && <span className="text-[9px] px-1 rounded bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))]">{r.channel}</span>}
            <span className="text-[9px] text-[hsl(var(--text-muted))]">Remind: {r.remind_before_hours || 0}h before</span>
          </div>
          <p className="text-[9px] font-mono mt-0.5" style={{ color: toneColor }}>
            {r.date} {r.startTime} • {timeLabel(r.starts_in_minutes)}
          </p>
        </div>
        <button
          onClick={() => onPush(r)}
          disabled={pushing}
          className="shrink-0 p-1.5 rounded bg-[#E63946]/15 text-[#E63946] hover:bg-[#E63946]/25 transition-all disabled:opacity-40"
          title="Push reminder to Telegram now"
          data-testid={`btn-push-tg-${r.id}`}
        >
          {pushing ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
        </button>
      </div>
    </div>
  );
}

function GuideStep({ n, icon: Icon, color, title, body }) {
  return (
    <div className="flex gap-3 p-2.5 rounded-lg bg-[hsl(var(--surface))]">
      <div className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-bold text-[10px]" style={{ background: `${color}20`, color }}>
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <Icon size={11} style={{ color }} />
          <p className="text-[11px] font-semibold text-white">{title}</p>
        </div>
        <div className="text-[10px] text-[hsl(var(--text-muted))] leading-relaxed">
          {body}
        </div>
      </div>
    </div>
  );
}
